import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, firstValueFrom, map, of, take, timeout } from 'rxjs';
import { SupportModalComponent, SupportModalData } from 'app/app-modules/modal';
import { UserApiClient } from 'app/core/api-clients';

// The dialog waits for the name lookup before opening, so keep the wait short: if the result
// isn't back within this budget we stop waiting, open with a blank name, and let the user type.
// Kept at ~1s so the Contact Us button always feels responsive even on a slow/hung backend.
const NAME_FETCH_TIMEOUT_MS = 1000;

@Injectable()
export class SupportModalService {
  private readonly _dialogService = inject(DialogService);

  private readonly _translateService = inject(TranslateService);

  private readonly _userApiClient = inject(UserApiClient);

  /**
   * Opens the Contact Us dialog. Resolves with true when the request was submitted successfully,
   * or false if the dialog was dismissed.
   */
  async open(): Promise<boolean> {
    // Resolve the signed-in user's name up front so the dialog can prefill it. Neither an error
    // nor a slow/hung backend must block support: on failure or timeout, fall back to an empty
    // name and let the user type it.
    const userName: string = await firstValueFrom(
      this._userApiClient.getCurrentUserName().pipe(
        take(1),
        timeout(NAME_FETCH_TIMEOUT_MS),
        map(name => name.fullName?.trim() ?? ''),
        catchError(() => of(''))
      ),
      { defaultValue: '' }
    );

    const data: SupportModalData = { name: userName };

    return await firstValueFrom(
      this._dialogService
        .open(SupportModalComponent, {
          showHeader: true,
          header: await firstValueFrom(this._translateService.get('general.contactSupport')),
          modal: true,
          closable: true,
          closeOnEscape: true,
          width: '40rem',
          data,
          breakpoints: {
            '640px': 'calc(100vw - 1rem)',
          },
        })
        .onClose.pipe(
          take(1),
          map(submitted => submitted === true)
        ),
      { defaultValue: false }
    );
  }
}
